import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { colors } from '../theme';
import VerifiedIcon from '@mui/icons-material/Verified';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import BoltIcon from '@mui/icons-material/Bolt';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import NatureIcon from '@mui/icons-material/Nature';

function SustainabilitySection() {
  const benefits = [
    'Reduce carbon footprint with solar-powered charging',
    'Lower operational costs with peak shaving',
    'Energy independence with ESS integration',
    'Future-proof infrastructure',
    'Compliance with Singapore green initiatives',
  ];

  const standards = [
    {
      code: 'LTA EVCO',
      description: 'Licensed EV Charging Operator',
    },
    {
      code: 'EMA',
      description: 'Energy Market Authority Compliant',
    },
    {
      code: 'BCA',
      description: 'Building & Construction Authority',
    },
    {
      code: 'SCDF',
      description: 'Singapore Civil Defence Force',
    },
    {
      code: 'NEA',
      description: 'National Environment Agency',
    },
  ];

  return (
    <Box
      id="sustainability"
      sx={{
        py: { xs: 6, md: 8 },
        background: `linear-gradient(135deg, ${colors.black} 0%, #1a1a1a 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.05,
          backgroundImage: `
            radial-gradient(circle, ${colors.green} 1px, transparent 1px)
          `,
          backgroundSize: '30px 30px',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <NatureIcon
            sx={{
              fontSize: 60,
              color: colors.green,
              mb: 2,
            }}
          />
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.75rem' },
              fontWeight: 800,
              mb: 2,
              color: colors.white,
            }}
          >
            Go Fully{' '}
            <Box component="span" sx={{ color: colors.green }}>
              Green
            </Box>
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: colors.white,
              opacity: 0.9,
              maxWidth: '700px',
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.2rem' },
            }}
          >
            Complete sustainable energy ecosystem with integrated solar, ESS, and
            EV charging solutions
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Left Column - Integration Visual */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 4,
                bgcolor: 'rgba(255,255,255,0.05)',
                border: `2px solid ${colors.green}40`,
                height: '100%',
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: colors.yellow,
                  fontWeight: 700,
                  mb: 3,
                }}
              >
                Integrated Energy Solution
              </Typography>

              {/* Visual Flow */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                  my: 4,
                }}
              >
                {/* Solar */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 3,
                    borderRadius: 3,
                    bgcolor: `${colors.yellow}15`,
                    border: `2px solid ${colors.yellow}60`,
                  }}
                >
                  <WbSunnyIcon
                    sx={{ fontSize: 40, color: colors.yellow, mr: 2 }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        color: colors.yellow,
                        fontWeight: 700,
                        fontSize: '1.1rem',
                      }}
                    >
                      Solar Panels
                    </Typography>
                    <Typography sx={{ color: colors.white, fontSize: '0.9rem' }}>
                      Clean energy generation
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ textAlign: 'center', color: colors.green }}>
                  <Typography sx={{ fontSize: '1.5rem' }}>↓</Typography>
                </Box>

                {/* ESS */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 3,
                    borderRadius: 3,
                    bgcolor: `${colors.green}15`,
                    border: `2px solid ${colors.green}60`,
                  }}
                >
                  <BatteryChargingFullIcon
                    sx={{ fontSize: 40, color: colors.green, mr: 2 }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        color: colors.green,
                        fontWeight: 700,
                        fontSize: '1.1rem',
                      }}
                    >
                      Energy Storage System
                    </Typography>
                    <Typography sx={{ color: colors.white, fontSize: '0.9rem' }}>
                      Store & stabilize power
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ textAlign: 'center', color: colors.green }}>
                  <Typography sx={{ fontSize: '1.5rem' }}>↓</Typography>
                </Box>

                {/* EV Charging */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 3,
                    borderRadius: 3,
                    bgcolor: `${colors.yellow}15`,
                    border: `2px solid ${colors.yellow}60`,
                  }}
                >
                  <BoltIcon sx={{ fontSize: 40, color: colors.yellow, mr: 2 }} />
                  <Box>
                    <Typography
                      sx={{
                        color: colors.yellow,
                        fontWeight: 700,
                        fontSize: '1.1rem',
                      }}
                    >
                      EV Charging Stations
                    </Typography>
                    <Typography sx={{ color: colors.white, fontSize: '0.9rem' }}>
                      Reliable, green charging
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Benefits List */}
              <Box sx={{ mt: 4 }}>
                <Typography
                  sx={{
                    color: colors.white,
                    fontWeight: 700,
                    mb: 2,
                    fontSize: '1.1rem',
                  }}
                >
                  Key Benefits:
                </Typography>
                <List sx={{ py: 0 }}>
                  {benefits.map((benefit, index) => (
                    <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 35 }}>
                        <CheckCircleIcon
                          sx={{
                            color: colors.green,
                            fontSize: 20,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={benefit}
                        primaryTypographyProps={{
                          fontSize: '0.95rem',
                          color: colors.white,
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>

              {/* Solar Partner Note */}
              <Box
                sx={{
                  mt: 4,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'rgba(255,211,0,0.1)',
                  borderLeft: `4px solid ${colors.yellow}`,
                }}
              >
                <Typography
                  sx={{
                    color: colors.white,
                    fontSize: '0.9rem',
                    fontWeight: 600,
                  }}
                >
                  <WbSunnyIcon
                    sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }}
                  />
                  Solar installations powered by{' '}
                  <Box
                    component="span"
                    sx={{ color: colors.yellow, fontWeight: 700 }}
                  >
                    Solateks
                  </Box>
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Right Column - Compliance & Standards */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 4,
                bgcolor: 'rgba(255,255,255,0.05)',
                border: `2px solid ${colors.yellow}40`,
                height: '100%',
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: colors.yellow,
                  fontWeight: 700,
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <VerifiedIcon sx={{ mr: 1, fontSize: 30 }} />
                Compliance & Standards
              </Typography>

              <Typography
                sx={{
                  color: colors.white,
                  mb: 4,
                  lineHeight: 1.7,
                  fontSize: '1rem',
                }}
              >
                Our solutions are fully compliant with all Singapore regulations
                and international standards, ensuring safety, reliability, and
                legal operation.
              </Typography>

              {/* Standards Grid */}
              <Grid container spacing={2}>
                {standards.map((standard, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: 3,
                        bgcolor: 'rgba(0,200,83,0.1)',
                        border: `1px solid ${colors.green}40`,
                        height: '100%',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          bgcolor: 'rgba(0,200,83,0.15)',
                          border: `1px solid ${colors.green}`,
                          transform: 'translateY(-4px)',
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          color: colors.green,
                          fontWeight: 700,
                          fontSize: '1.1rem',
                          mb: 0.5,
                        }}
                      >
                        {standard.code}
                      </Typography>
                      <Typography
                        sx={{
                          color: colors.white,
                          fontSize: '0.85rem',
                          opacity: 0.9,
                        }}
                      >
                        {standard.description}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Licensed Badge */}
              <Box
                sx={{
                  mt: 4,
                  p: 3,
                  borderRadius: 3,
                  bgcolor: `${colors.yellow}20`,
                  border: `2px solid ${colors.yellow}`,
                  textAlign: 'center',
                }}
              >
                <VerifiedIcon
                  sx={{
                    fontSize: 50,
                    color: colors.yellow,
                    mb: 1,
                  }}
                />
                <Typography
                  sx={{
                    color: colors.white,
                    fontWeight: 700,
                    fontSize: '1.2rem',
                    mb: 0.5,
                  }}
                >
                  Licensed LTA EVCO Operator
                </Typography>
                <Typography
                  sx={{
                    color: colors.white,
                    fontSize: '0.9rem',
                    opacity: 0.9,
                  }}
                >
                  Authorized to install and operate EV charging infrastructure in
                  Singapore
                </Typography>
              </Box>

              {/* Compliance Badges */}
              <Box
                sx={{
                  mt: 4,
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 3,
                  width: '100%',
                }}
              >
                {[
                  { name: 'LTA', logo: '/lta-logo.png' },
                  { name: 'EMA', logo: '/ema-logo.png' },
                  { name: 'BCA', logo: '/bca-logo.png' },
                ].map((badge) => (
                  <Box
                    key={badge.name}
                    sx={{
                      flex: 1,
                      height: 120,
                      borderRadius: 2,
                      bgcolor: colors.white,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                      p: 2,
                    }}
                  >
                    <Box
                      component="img"
                      src={badge.logo}
                      alt={badge.name}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = `<span style="font-weight: 700; font-size: 0.9rem; color: #000;">${badge.name}</span>`;
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default SustainabilitySection;
